import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { UserRole } from '../types';

export interface Permission {
  id: string;
  key: string;
  label: string;
  module: string;
  description: string | null;
}

export interface Role {
  id: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  /** Underlying Postgres user_role enum value this role maps to for DB/RLS purposes. */
  baseRole: UserRole;
  permissionIds: string[];
}

/**
 * Functional tiers a custom role can "act as" - grants real database/RLS
 * access matching that tier (e.g. Site Engineer: attendance, checklists,
 * assigned tasks, project-team visibility), on top of whatever page
 * permissions are separately checked for the role. Admin is excluded (too
 * powerful to hand out via a custom role); Customer/Vendor are excluded
 * (portal-only, different login flow).
 */
export const FUNCTIONAL_TIER_OPTIONS: { value: UserRole; label: string }[] = [
  { value: 'custom_staff', label: 'None (custom staff)' },
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'site_manager', label: 'Site Manager' },
  { value: 'finance', label: 'Finance' },
  { value: 'manager', label: 'Manager' },
  { value: 'sales', label: 'Sales' },
  { value: 'organizer', label: 'Organizer' },
  { value: 'site_engineer', label: 'Site Engineer' },
  { value: 'freelancer', label: 'Freelancer' },
];

interface RawRoleRow {
  id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  base_role: UserRole;
  role_permissions: { permission_id: string }[] | null;
}

interface RolesState {
  roles: Role[];
  permissions: Permission[];
  loading: boolean;
  error: string | null;

  fetchRoles: () => Promise<void>;
  fetchPermissions: () => Promise<void>;
  createRole: (
    name: string,
    description: string,
    permissionIds: string[],
    baseRole?: UserRole
  ) => Promise<void>;
  updateRolePermissions: (roleId: string, permissionIds: string[]) => Promise<void>;
  renameRole: (roleId: string, name: string, description: string) => Promise<void>;
  /** Changes a custom role's functional tier and syncs the DB role enum for every currently-assigned user. */
  updateRoleBaseRole: (roleId: string, baseRole: UserRole) => Promise<void>;
  deleteRole: (roleId: string) => Promise<void>;
  createPermission: (key: string, label: string, module: string) => Promise<void>;
}

function assertEditable(role: Role | undefined) {
  if (!role) throw new Error('Role not found');
  if (role.isSystem) throw new Error('System roles cannot be modified');
}

export const useRolesStore = create<RolesState>((set, get) => ({
  roles: [],
  permissions: [],
  loading: false,
  error: null,

  fetchRoles: async () => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('roles')
        .select('id,name,description,is_system,base_role,role_permissions(permission_id)')
        .order('name');

      if (error) throw error;

      const roles: Role[] = ((data ?? []) as RawRoleRow[]).map((r) => ({
        id: r.id,
        name: r.name,
        description: r.description,
        isSystem: r.is_system,
        baseRole: r.base_role,
        permissionIds: (r.role_permissions ?? []).map((rp) => rp.permission_id),
      }));

      set({ roles, loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  },

  fetchPermissions: async () => {
    try {
      const { data, error } = await supabase
        .from('permissions')
        .select('id,key,label,module,description')
        .order('module')
        .order('label');

      if (error) throw error;
      set({ permissions: (data ?? []) as Permission[] });
    } catch (error) {
      set({ error: (error as Error).message });
    }
  },

  createRole: async (name, description, permissionIds, baseRole = 'custom_staff') => {
    set({ loading: true, error: null });
    try {
      const trimmed = name.trim();
      if (!trimmed) throw new Error('Role name is required');

      const { data: role, error: roleError } = await supabase
        .from('roles')
        .insert([{ name: trimmed, description, is_system: false, base_role: baseRole }])
        .select('id,name,description,is_system,base_role')
        .single();

      if (roleError) throw roleError;

      if (permissionIds.length > 0) {
        const { error: rpError } = await supabase
          .from('role_permissions')
          .insert(permissionIds.map((permission_id) => ({ role_id: role.id, permission_id })));
        if (rpError) throw rpError;
      }

      await get().fetchRoles();
      set({ loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  updateRolePermissions: async (roleId, permissionIds) => {
    const role = get().roles.find((r) => r.id === roleId);
    assertEditable(role);
    set({ loading: true, error: null });
    try {
      const { error: deleteError } = await supabase
        .from('role_permissions')
        .delete()
        .eq('role_id', roleId);
      if (deleteError) throw deleteError;

      if (permissionIds.length > 0) {
        const { error: insertError } = await supabase
          .from('role_permissions')
          .insert(permissionIds.map((permission_id) => ({ role_id: roleId, permission_id })));
        if (insertError) throw insertError;
      }

      await get().fetchRoles();
      set({ loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  renameRole: async (roleId, name, description) => {
    const role = get().roles.find((r) => r.id === roleId);
    assertEditable(role);
    set({ loading: true, error: null });
    try {
      const trimmed = name.trim();
      if (!trimmed) throw new Error('Role name is required');

      const { error } = await supabase
        .from('roles')
        .update({ name: trimmed, description, updated_at: new Date().toISOString() })
        .eq('id', roleId);
      if (error) throw error;

      await get().fetchRoles();
      set({ loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  updateRoleBaseRole: async (roleId, baseRole) => {
    const role = get().roles.find((r) => r.id === roleId);
    assertEditable(role);
    set({ loading: true, error: null });
    try {
      const { error: roleError } = await supabase
        .from('roles')
        .update({ base_role: baseRole, updated_at: new Date().toISOString() })
        .eq('id', roleId);
      if (roleError) throw roleError;

      // Keep every currently-assigned user's DB role enum in sync with the
      // role's new functional tier - RLS and the ~28 legacy role-check files
      // read `users.role` directly, not role_id.
      const { error: usersError } = await supabase
        .from('users')
        .update({ role: baseRole })
        .eq('role_id', roleId);
      if (usersError) throw usersError;

      await get().fetchRoles();
      set({ loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  deleteRole: async (roleId) => {
    const role = get().roles.find((r) => r.id === roleId);
    assertEditable(role);
    set({ loading: true, error: null });
    try {
      const { count, error: usageError } = await supabase
        .from('users')
        .select('id', { count: 'exact', head: true })
        .eq('role_id', roleId);
      if (usageError) throw usageError;
      if ((count ?? 0) > 0) {
        throw new Error('Cannot delete a role that is currently assigned to users');
      }

      const { error } = await supabase.from('roles').delete().eq('id', roleId);
      if (error) throw error;

      set((state) => ({ roles: state.roles.filter((r) => r.id !== roleId), loading: false }));
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  createPermission: async (key, label, module) => {
    set({ loading: true, error: null });
    try {
      const trimmedKey = key.trim();
      const trimmedLabel = label.trim();
      const trimmedModule = module.trim();
      if (!trimmedKey || !trimmedLabel || !trimmedModule) {
        throw new Error('Key, label, and module are required');
      }

      const { error } = await supabase
        .from('permissions')
        .insert([{ key: trimmedKey, label: trimmedLabel, module: trimmedModule }]);
      if (error) throw error;

      await get().fetchPermissions();
      set({ loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },
}));
